"use client";

import { type RefObject, useEffect, useState } from "react";

export type ObjectPositionAxis =
  | { kind: "percent"; value: number }
  | { kind: "calc"; percent: number; px: number };

export type ObjectCoverMetrics = {
  renderedWidth: number;
  renderedHeight: number;
  offsetX: number;
  offsetY: number;
};

function resolveAxisOffset(
  axis: ObjectPositionAxis,
  containerSize: number,
  renderedSize: number
): number {
  const overflow = containerSize - renderedSize;
  if (axis.kind === "percent") {
    return overflow * (axis.value / 100);
  }
  return overflow * (axis.percent / 100) + axis.px;
}

export function getObjectCoverMetrics(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
  posX: ObjectPositionAxis,
  posY: ObjectPositionAxis
): ObjectCoverMetrics {
  const imageRatio = imageWidth / imageHeight;
  const containerRatio = containerWidth / containerHeight;

  const scale =
    containerRatio > imageRatio
      ? containerWidth / imageWidth
      : containerHeight / imageHeight;

  const renderedWidth = imageWidth * scale;
  const renderedHeight = imageHeight * scale;

  return {
    renderedWidth,
    renderedHeight,
    offsetX: resolveAxisOffset(posX, containerWidth, renderedWidth),
    offsetY: resolveAxisOffset(posY, containerHeight, renderedHeight),
  };
}

export function imageAnchorToPoint(
  metrics: ObjectCoverMetrics,
  anchorX: number,
  anchorY: number
): { x: number; y: number } {
  return {
    x: metrics.offsetX + anchorX * metrics.renderedWidth,
    y: metrics.offsetY + anchorY * metrics.renderedHeight,
  };
}

function applyScaleTransform(
  point: { x: number; y: number },
  containerWidth: number,
  containerHeight: number,
  scale: number,
  originX: number,
  originY: number
): { x: number; y: number } {
  const pivotX = originX * containerWidth;
  const pivotY = originY * containerHeight;
  return {
    x: pivotX + (point.x - pivotX) * scale,
    y: pivotY + (point.y - pivotY) * scale,
  };
}

type UseObjectCoverAnchorOptions = {
  imageWidth: number;
  imageHeight: number;
  anchorX: number;
  anchorY: number;
  objectPosition: { x: ObjectPositionAxis; y: ObjectPositionAxis };
  scale?: number;
  transformOrigin?: { x: number; y: number };
};

export function useObjectCoverAnchor(
  containerRef: RefObject<HTMLElement | null>,
  options: UseObjectCoverAnchorOptions
): { x: number; y: number } | null {
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const update = () => {
      // Use the layout box (clientWidth/clientHeight), not getBoundingClientRect,
      // so the anchor is stable across CSS transforms applied to the container
      // (e.g. the gsap scale entrance animation on the closing-section background).
      // getBoundingClientRect() returns the post-transform visual rect, and
      // ResizeObserver does not fire on transform changes, which would otherwise
      // make the anchor jump on the next resize after a transform settles.
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (width <= 0 || height <= 0) return;

      const metrics = getObjectCoverMetrics(
        width,
        height,
        options.imageWidth,
        options.imageHeight,
        options.objectPosition.x,
        options.objectPosition.y
      );

      setPoint(
        applyScaleTransform(
          imageAnchorToPoint(metrics, options.anchorX, options.anchorY),
          width,
          height,
          options.scale ?? 1,
          options.transformOrigin?.x ?? 0.5,
          options.transformOrigin?.y ?? 0.5
        )
      );
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(container);
    window.addEventListener("resize", update);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [
    containerRef,
    options.imageWidth,
    options.imageHeight,
    options.anchorX,
    options.anchorY,
    options.objectPosition.x,
    options.objectPosition.y,
    options.scale,
    options.transformOrigin?.x,
    options.transformOrigin?.y,
  ]);

  return point;
}
